import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link2, Loader2, RefreshCw, Grid3x3 } from 'lucide-react';
import { toast } from 'sonner';
import SystemMapAddConnectorDialog from './SystemMapAddConnectorDialog';
import SystemMapAddMenu from './SystemMapAddMenu';
import SystemMapAddNodeDialog from './SystemMapAddNodeDialog';
import SystemMapCanvas from './SystemMapCanvas';
import SystemMapDetailPanel from './SystemMapDetailPanel';
import SystemMapSearch from './SystemMapSearch';
import {
  MAP_NODE_KINDS,
  defaultNewNodePosition,
  fetchSystemMap,
  kindLabel,
  searchMapNodes,
  type MapNodeKind,
  type MapNodeRow,
  type MapEdgeRow,
  type SystemMapBundle,
  mergeEdgeMetadata,
  updateMapEdge,
} from '../../lib/systemMap';
import type { MapEdgeEndpoint } from './SystemMapEdgeActionsContext';
import { snapSystemMapPosition } from './systemMapNodeGeometry';

const SNAP_TO_GRID_STORAGE_KEY = 'system-map-snap-to-grid';

function readSnapToGridPreference(): boolean {
  try {
    return localStorage.getItem(SNAP_TO_GRID_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function toggleKindFilter(current: Set<MapNodeKind>, kind: MapNodeKind): Set<MapNodeKind> {
  const next = new Set(current);
  if (next.has(kind)) next.delete(kind);
  else next.add(kind);
  return next;
}

export default function SystemMapView() {
  const [bundle, setBundle] = useState<SystemMapBundle | null>(null);
  const [nodeRows, setNodeRows] = useState<MapNodeRow[]>([]);
  const [edgeRows, setEdgeRows] = useState<MapEdgeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [kindFilters, setKindFilters] = useState<Set<MapNodeKind>>(() => new Set(MAP_NODE_KINDS));
  const [showConnectorLabels, setShowConnectorLabels] = useState(true);
  const [snapToGrid, setSnapToGrid] = useState(readSnapToGridPreference);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [selectedWaypointIndex, setSelectedWaypointIndex] = useState<number | null>(null);
  const [selectedEndpoint, setSelectedEndpoint] = useState<MapEdgeEndpoint | null>(null);
  const [fitAllSignal, setFitAllSignal] = useState(0);
  const [goToNodeId, setGoToNodeId] = useState<string | null>(null);
  const [addNodeOpen, setAddNodeOpen] = useState(false);
  const [connectMode, setConnectMode] = useState(false);
  const [connectSourceNodeId, setConnectSourceNodeId] = useState<string | null>(null);
  const [connectorDialogOpen, setConnectorDialogOpen] = useState(false);
  const [connectorDialogSourceId, setConnectorDialogSourceId] = useState<string | null>(null);
  const [connectorDialogTargetId, setConnectorDialogTargetId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchSystemMap();
      setBundle(data);
      setNodeRows(data.nodes);
      setEdgeRows(data.edges);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to load system map');
      setBundle(null);
      setNodeRows([]);
      setEdgeRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    try {
      localStorage.setItem(SNAP_TO_GRID_STORAGE_KEY, String(snapToGrid));
    } catch {
      /* private mode / blocked storage */
    }
  }, [snapToGrid]);

  useEffect(() => {
    if (selectedNodeId) {
      const node = nodeRows.find((n) => n.id === selectedNodeId);
      if (node && !kindFilters.has(node.kind)) {
        setSelectedNodeId(null);
      }
    }

    if (selectedEdgeId) {
      const edge = edgeRows.find((e) => e.id === selectedEdgeId);
      if (edge) {
        const source = nodeRows.find((n) => n.id === edge.source_node_id);
        const target = nodeRows.find((n) => n.id === edge.target_node_id);
        const edgeVisible =
          source &&
          target &&
          kindFilters.has(source.kind) &&
          kindFilters.has(target.kind);
        if (!edgeVisible) {
          setSelectedEdgeId(null);
          setSelectedWaypointIndex(null);
        }
      }
    }
  }, [kindFilters, selectedNodeId, selectedEdgeId, nodeRows, edgeRows]);

  const visibleNodeRows = useMemo(
    () => nodeRows.filter((row) => kindFilters.has(row.kind)),
    [nodeRows, kindFilters],
  );

  const matchingNodes = useMemo(
    () => searchMapNodes(visibleNodeRows, searchQuery),
    [visibleNodeRows, searchQuery],
  );

  const selectedNode = useMemo(
    () => nodeRows.find((n) => n.id === selectedNodeId) ?? null,
    [nodeRows, selectedNodeId],
  );

  const selectedEdge = useMemo(
    () => edgeRows.find((e) => e.id === selectedEdgeId) ?? null,
    [edgeRows, selectedEdgeId],
  );

  const handleNodeUpdated = useCallback((updated: MapNodeRow) => {
    setNodeRows((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
  }, []);

  const handleEdgeUpdated = useCallback((updated: MapEdgeRow) => {
    setEdgeRows((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
    setSelectedWaypointIndex(null);
  }, []);

  const handleEdgeDeleted = useCallback((edgeId: string) => {
    setEdgeRows((prev) => prev.filter((row) => row.id !== edgeId));
    setSelectedEdgeId(null);
    setSelectedWaypointIndex(null);
  }, []);

  const openConnectorDialog = useCallback(
    (sourceId: string | null, targetId: string | null) => {
      setConnectorDialogSourceId(sourceId);
      setConnectorDialogTargetId(targetId);
      setConnectorDialogOpen(true);
    },
    [],
  );

  const handleConnectPair = useCallback(
    (sourceNodeId: string, targetNodeId: string) => {
      openConnectorDialog(sourceNodeId, targetNodeId);
    },
    [openConnectorDialog],
  );

  const handleEdgeCreated = useCallback((edge: MapEdgeRow) => {
    setEdgeRows((prev) => [...prev, edge]);
    setConnectSourceNodeId(null);
    setConnectMode(false);
    setSelectedNodeId(null);
    setSelectedEdgeId(edge.id);
    setSelectedWaypointIndex(null);
    setSelectedEndpoint(null);
  }, []);

  const toggleConnectMode = useCallback(() => {
    setConnectMode((prev) => {
      const next = !prev;
      if (!next) {
        setConnectSourceNodeId(null);
        setSelectedNodeId(null);
      } else {
        setSelectedEdgeId(null);
        setSelectedWaypointIndex(null);
        setSelectedEndpoint(null);
      }
      return next;
    });
  }, []);

  const handleNodeCreated = useCallback((node: MapNodeRow) => {
    setNodeRows((prev) => [...prev, node]);
    setKindFilters((prev) => new Set([...prev, node.kind]));
    setSelectedEdgeId(null);
    setSelectedWaypointIndex(null);
    setSelectedEndpoint(null);
    setSelectedNodeId(node.id);
    setGoToNodeId(node.id);
  }, []);

  const newNodePosition = useMemo(() => {
    if (!bundle) return { x: 320, y: 200 };
    const pos = defaultNewNodePosition(bundle.map.viewport, nodeRows.length);
    return snapToGrid ? snapSystemMapPosition(pos.x, pos.y) : pos;
  }, [bundle, nodeRows.length, snapToGrid]);

  const existingSlugs = useMemo(() => nodeRows.map((n) => n.slug), [nodeRows]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (connectSourceNodeId) {
          setConnectSourceNodeId(null);
          setSelectedNodeId(null);
          return;
        }
        if (connectMode) {
          setConnectMode(false);
          return;
        }
      }

      if (event.key !== 'Delete' && event.key !== 'Backspace') return;
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }
      if (!selectedEdgeId || selectedWaypointIndex == null) return;
      const edge = edgeRows.find((row) => row.id === selectedEdgeId);
      if (!edge) return;
      event.preventDefault();
      void updateMapEdge(selectedEdgeId, {
        metadata: mergeEdgeMetadata(edge.metadata, { waypoints: [] }),
      })
        .then(handleEdgeUpdated)
        .catch((e: unknown) => {
          toast.error(e instanceof Error ? e.message : 'Failed to reset route');
        });
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [connectMode, connectSourceNodeId, edgeRows, handleEdgeUpdated, selectedEdgeId, selectedWaypointIndex]);

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && matchingNodes.length > 0) {
      setGoToNodeId(matchingNodes[0].id);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!bundle) {
    return (
      <div className="max-w-lg mx-auto mt-12 text-center space-y-3">
        <p className="text-gray-600 dark:text-gray-400">System map not found.</p>
        <p className="text-sm text-gray-500">
          Run the Supabase migration <code className="text-xs">20260614120000_system_map.sql</code>{' '}
          and refresh.
        </p>
        <button
          type="button"
          onClick={() => void load()}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
        >
          <RefreshCw className="w-4 h-4" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] -mx-4 -my-4 sm:-mx-6 sm:-my-6">
      <header className="shrink-0 px-4 sm:px-6 py-2 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
        <div className="flex items-center gap-2 min-w-0 overflow-x-auto">
          <div onKeyDown={handleSearchKeyDown} className="flex items-center gap-1.5 shrink-0">
            <div className="w-44 sm:w-52">
              <SystemMapSearch
                query={searchQuery}
                onQueryChange={setSearchQuery}
                matchCount={matchingNodes.length}
                totalCount={visibleNodeRows.length}
              />
            </div>
            {searchQuery.trim() ? (
              <span className="text-[11px] text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {matchingNodes.length}/{visibleNodeRows.length}
              </span>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => void load()}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 shrink-0 whitespace-nowrap"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setShowConnectorLabels((v) => !v)}
            aria-pressed={showConnectorLabels}
            title={showConnectorLabels ? 'Hide connector labels' : 'Show connector labels'}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-lg border transition-colors shrink-0 whitespace-nowrap ${
              showConnectorLabels
                ? 'bg-emerald-600 border-emerald-600 text-white'
                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            Labels
          </button>
          <button
            type="button"
            onClick={() => setSnapToGrid((v) => !v)}
            aria-pressed={snapToGrid}
            title={snapToGrid ? 'Snap to grid on (20px) — click to turn off' : 'Snap to grid off — click to align nodes to 20px grid'}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-lg border transition-colors shrink-0 whitespace-nowrap ${
              snapToGrid
                ? 'bg-emerald-600 border-emerald-600 text-white'
                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <Grid3x3 className="w-3.5 h-3.5" />
            Snap
          </button>
          <button
            type="button"
            onClick={toggleConnectMode}
            aria-pressed={connectMode}
            title={connectMode ? 'Exit connect mode (Esc)' : 'Connect nodes — click source then target, or drag green dots'}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-lg border transition-colors shrink-0 whitespace-nowrap ${
              connectMode
                ? 'bg-sky-600 border-sky-600 text-white'
                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <Link2 className="w-3.5 h-3.5" />
            Connect
          </button>
          <SystemMapAddMenu
            onAddNode={() => setAddNodeOpen(true)}
            onAddConnector={() => openConnectorDialog(null, null)}
            onFitAll={() => setFitAllSignal((n) => n + 1)}
          />
          <div className="h-5 w-px bg-gray-200 dark:bg-gray-700 shrink-0" aria-hidden />
          {MAP_NODE_KINDS.map((kind) => {
            const active = kindFilters.has(kind);
            return (
              <button
                key={kind}
                type="button"
                onClick={() => setKindFilters((prev) => toggleKindFilter(prev, kind))}
                aria-pressed={active}
                title={active ? `${kindLabel(kind)} visible — click to hide` : `${kindLabel(kind)} hidden — click to show`}
                className={`px-2.5 py-1.5 text-xs rounded-full border transition-colors shrink-0 whitespace-nowrap ${
                  active
                    ? 'bg-emerald-600 border-emerald-600 text-white'
                    : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                {kindLabel(kind)}
              </button>
            );
          })}
          {kindFilters.size < MAP_NODE_KINDS.length ? (
            <button
              type="button"
              onClick={() => setKindFilters(new Set(MAP_NODE_KINDS))}
              className="px-2.5 py-1.5 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 shrink-0 whitespace-nowrap"
            >
              Show all
            </button>
          ) : null}
        </div>
        {connectMode ? (
          <p className="mt-1.5 text-xs text-sky-700 dark:text-sky-300">
            {connectSourceNodeId
              ? 'Click the target node — or drag from a green dot. Esc clears source.'
              : 'Click the source node — or drag from a green dot to another node. Esc exits.'}
          </p>
        ) : null}
      </header>

      <div className="flex flex-1 min-h-0">
        <SystemMapCanvas
          map={bundle.map}
          nodeRows={nodeRows}
          edgeRows={edgeRows}
          searchQuery={searchQuery}
          kindFilters={kindFilters}
          selectedNodeId={selectedNodeId}
          selectedEdgeId={selectedEdgeId}
          onSelectNode={setSelectedNodeId}
          onSelectEdge={setSelectedEdgeId}
          onNodeRowUpdated={setNodeRows}
          onEdgeRowUpdated={setEdgeRows}
          selectedWaypointIndex={selectedWaypointIndex}
          onSelectWaypoint={setSelectedWaypointIndex}
          selectedEndpoint={selectedEndpoint}
          onSelectEndpoint={setSelectedEndpoint}
          showConnectorLabels={showConnectorLabels}
          snapToGrid={snapToGrid}
          connectMode={connectMode}
          connectSourceNodeId={connectSourceNodeId}
          onConnectSourceSelect={setConnectSourceNodeId}
          onConnectPair={handleConnectPair}
          fitAllSignal={fitAllSignal}
          goToNodeId={goToNodeId}
          onGoToComplete={() => setGoToNodeId(null)}
        />
        <SystemMapDetailPanel
          node={selectedNode}
          edge={selectedEdge}
          nodeRows={nodeRows}
          selectedWaypointIndex={selectedWaypointIndex}
          onNodeUpdated={handleNodeUpdated}
          onEdgeUpdated={handleEdgeUpdated}
          onEdgeDeleted={handleEdgeDeleted}
          onMapReload={() => void load()}
          onClose={() => {
            setSelectedNodeId(null);
            setSelectedEdgeId(null);
            setSelectedWaypointIndex(null);
            setSelectedEndpoint(null);
          }}
        />
      </div>

      <SystemMapAddNodeDialog
        open={addNodeOpen}
        mapId={bundle.map.id}
        existingSlugs={existingSlugs}
        defaultX={newNodePosition.x}
        defaultY={newNodePosition.y}
        onClose={() => setAddNodeOpen(false)}
        onCreated={handleNodeCreated}
      />

      <SystemMapAddConnectorDialog
        open={connectorDialogOpen}
        mapId={bundle.map.id}
        nodes={visibleNodeRows}
        initialSourceId={connectorDialogSourceId}
        initialTargetId={connectorDialogTargetId}
        onClose={() => {
          setConnectorDialogOpen(false);
          setConnectorDialogSourceId(null);
          setConnectorDialogTargetId(null);
          setConnectSourceNodeId(null);
        }}
        onCreated={handleEdgeCreated}
      />
    </div>
  );
}
